import axiosNest from '../../api/axiosNest';
import { fmtCalendarDate, fmtDate } from '../../shared/Panel';

/**
 * Korekty kartoteki pojazdu — kształt odpowiedzi i wywołania API
 * (22.10.2026, przełącznik `fleetCorrections`, S-22.10-03; API:
 * `apps/api/src/fleet-corrections/`).
 *
 * Makieta FTL p. Mariusza (procedura FTL-08): opiekun pojazdu albo Team
 * Leader zmienia kierowcę, naczepę i terminy uprawnień auta. Korekta ma
 * autora, czas i powód; wartość różna od TMS ma znacznik „≠ TMS” (w dymku:
 * kto, kiedy, powód, wartość w TMS) i „przywróć z TMS”. Import TMS korekty
 * nie nadpisuje.
 */

/** Klucz przełącznika — jak w API (`fleetFeatures.ts`). */
export { FLEET_CORRECTIONS } from './fleetFeatures';

/** Pole korekty — dane z API, nie tłumaczymy. */
export type CorrectionField = 'driver' | 'trailer' | 'eligibility';

/** Aktywna korekta z karty pojazdu (`GET /cars/:id` → `corrections`). */
export interface CorrectionView {
  id: number;
  field: CorrectionField;
  eligibilityId: number | null;
  valueLabel: string;
  tmsLabel: string;
  tmsAtCorrection: string;
  warning: string | null;
  reason: string;
  createdByLogin: string | null;
  createdAt: string;
}

export interface CorrectionAccess {
  allowed: boolean;
  refusal: string | null;
  note: string | null;
}

export interface CorrectionEffects {
  variants: Array<{ orderId: number; number: string; author: string | null }>;
  assignments: Array<{ orderId: number; number: string }>;
}

export interface CorrectionContext {
  access: CorrectionAccess;
  effects: CorrectionEffects;
}

export interface CorrectionResult {
  changed: boolean;
  releasedVariants: string[];
  corrections: CorrectionView[];
  note: string | null;
}

const base = (carId: number | string) => `/cars/${carId}/corrections`;

/** Korekty z rekordu karty; brak pola (przełącznik wyłączony) — pusta lista. */
export function correctionsOf(record: Record<string, unknown> | undefined): CorrectionView[] {
  const list = record?.corrections;
  return Array.isArray(list) ? (list as CorrectionView[]) : [];
}

/** Korekta pola — kierowca, naczepa albo termin danego uprawnienia. */
export function correctionFor(
  corrections: readonly CorrectionView[],
  field: CorrectionField,
  eligibilityId: number | null = null,
): CorrectionView | null {
  return (
    corrections.find((c) => c.field === field && (field !== 'eligibility' || c.eligibilityId === eligibilityId)) ??
    null
  );
}

/**
 * Dymek znacznika „≠ TMS” — jak w makiecie: kto i kiedy skorygował, powód,
 * wartość w TMS; gdy TMS zmienił się od korekty inaczej — „TMS ma teraz …”.
 */
export function correctionTooltip(c: CorrectionView): string[] {
  return [
    `Skorygowano w aplikacji: ${c.createdByLogin ?? 'nieznany użytkownik'}, ${fmtDate(c.createdAt)}`,
    `Powód: ${c.reason}`,
    `Wartość w TMS: ${c.tmsLabel}`,
    ...(c.warning ? [c.warning] : []),
  ];
}

/** Termin-data po polsku albo „bez terminu”. */
export const termText = (iso: string | null | undefined): string => (iso ? fmtCalendarDate(iso) : 'bez terminu');

export async function fetchCorrectionContext(carId: number | string): Promise<CorrectionContext> {
  const { data } = await axiosNest.get(`${base(carId)}/context`);
  const v = (data ?? {}) as Partial<CorrectionContext>;
  const effects: Partial<CorrectionEffects> = v.effects ?? {};
  return {
    access: v.access ?? { allowed: false, refusal: null, note: null },
    effects: {
      variants: Array.isArray(effects.variants) ? effects.variants : [],
      assignments: Array.isArray(effects.assignments) ? effects.assignments : [],
    },
  };
}

export async function saveDriverCorrection(
  carId: number | string,
  driverId: number | null,
  reason: string,
): Promise<CorrectionResult> {
  const { data } = await axiosNest.put(`${base(carId)}/driver`, { driverId, reason: reason.trim() });
  return data as CorrectionResult;
}

export async function saveTrailerCorrection(
  carId: number | string,
  registrationNumber: string | null,
  reason: string,
): Promise<CorrectionResult> {
  const plate = registrationNumber?.trim() ?? '';
  const { data } = await axiosNest.put(`${base(carId)}/trailer`, {
    registrationNumber: plate === '' ? null : plate,
    reason: reason.trim(),
  });
  return data as CorrectionResult;
}

export async function saveEligibilityCorrection(
  carId: number | string,
  eligibilityId: number,
  validUpTo: string,
  reason: string,
): Promise<CorrectionResult> {
  const { data } = await axiosNest.put(`${base(carId)}/eligibilities/${eligibilityId}`, {
    validUpTo,
    reason: reason.trim(),
  });
  return data as CorrectionResult;
}

export async function restoreFromTms(carId: number | string, correctionId: number): Promise<CorrectionResult> {
  const { data } = await axiosNest.post(`${base(carId)}/${correctionId}/restore`, {});
  return data as CorrectionResult;
}

/** Powód korekty — te same granice co API (`CORRECTION_REASON_MIN/MAX`). */
export const REASON_MIN = 10;
export const REASON_MAX = 500;

/** Komunikat po zapisie — z numerami zleceń, w których wygasły warianty. */
export function savedMessage(result: CorrectionResult, what: string): string {
  if (!result.changed) return 'Bez zmian — ta wartość już obowiązuje.';
  const released =
    result.releasedVariants.length > 0
      ? ` Wygasły warianty pojazdu: ${result.releasedVariants.join(', ')}.`
      : '';
  return `${what}.${released}${result.note ? ` ${result.note}` : ''}`;
}
